import { motion, useMotionValue, useTransform, PanInfo } from "framer-motion";
import { Star, Bookmark, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface MovieProvider { provider_id: number; provider_name: string; logo_path: string; }

export interface MovieData {
  id: number; title: string; synopsis: string; poster: string; year: number; runtime: number;
  rating: number; genres: string[]; isAd?: boolean; providers?: MovieProvider[];
  first_air_date?: string | null; last_air_date?: string | null; certification?: string;
  watch_link?: string;
}

interface SwipeCardProps {
  movie: MovieData; onSwipeLeft: () => void; onSwipeRight: () => void;
  onSeen: (rating: "loved" | "pretty_good" | "ok" | "not_great" | "hated") => void;
  seenRating?: "loved" | "pretty_good" | "ok" | "not_great" | "hated" | null;
  onStar: () => void; onBookmark: () => void; onReadMore?: () => void; isTop: boolean;
  isStarred?: boolean; isBookmarked?: boolean; direction?: "left" | "right";
  hideSeen?: boolean; hideBookmark?: boolean;
  mediaType?: "movie" | "tv"; onCurrentlyWatching?: () => void; isCurrentlyWatching?: boolean;
  onHide?: () => void; isHidden?: boolean;
}

const cardVariants = {
  enter: (direction: "left" | "right") => ({ x: direction === "left" ? 400 : -400, opacity: 0, scale: 0.92, rotateZ: direction === "left" ? 8 : -8 }),
  center: { x: 0, opacity: 1, scale: 1, rotateZ: 0, transition: { type: "spring" as const, stiffness: 300, damping: 30 } },
  exit: (direction: "left" | "right") => ({ x: direction === "left" ? -400 : 400, opacity: 0, scale: 0.92, rotateZ: direction === "left" ? -8 : 8, transition: { duration: 0.25, ease: "easeIn" as const } }),
};

const SwipeCard = ({ movie, onSwipeLeft, onSwipeRight, onSeen, seenRating, onStar, onBookmark, onReadMore, isTop, isStarred, isBookmarked, direction = "left", hideSeen = false, hideBookmark = false, mediaType, onCurrentlyWatching, isCurrentlyWatching, onHide, isHidden }: SwipeCardProps) => {
  const { t } = useTranslation();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const dragOpacity = useTransform(x, [-200, -100, 0, 100, 200], [0.5, 1, 1, 1, 0.5]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (info.offset.x > 100) onSwipeLeft();
    else if (info.offset.x < -100) onSwipeRight();
  };

  if (!isTop) {
    return (
      <motion.div className="absolute inset-0 flex items-center justify-center z-0 p-3" initial={{ scale: 0.93, opacity: 0.5 }} animate={{ scale: 0.93, opacity: 0.6 }}>
        <div className="w-full max-w-sm rounded-2xl bg-gradient-card border border-border overflow-hidden shadow-card max-h-full flex flex-col">
          <div className="relative flex-1 min-h-0 overflow-hidden"><img src={movie.poster} alt={movie.title} className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" /></div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div className="absolute inset-0 flex items-center justify-center z-10 px-3 py-2" custom={direction} variants={cardVariants} initial="enter" animate="center" exit="exit" style={{ x, rotate, opacity: dragOpacity }} drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.7} onDragEnd={handleDragEnd}>
      <div className="w-full max-w-sm rounded-2xl bg-gradient-card border border-border overflow-hidden shadow-card max-h-full flex flex-col" style={{ maxHeight: 'calc(100% - 0px)' }}>
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <img src={movie.poster} alt={movie.title} className="w-full h-full object-cover" loading="eager" />
          {movie.providers && movie.providers.length > 0 && (
            <div className="absolute top-3 left-3 flex items-center gap-1.5">
              {movie.providers.slice(0, 4).map((p) => (
                movie.watch_link ? (
                  <a key={p.provider_id} href={movie.watch_link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    <img src={p.logo_path} alt={p.provider_name} title={p.provider_name} className="w-7 h-7 rounded-lg shadow-md border border-border/30 hover:scale-110 transition-transform cursor-pointer" />
                  </a>
                ) : (
                  <img key={p.provider_id} src={p.logo_path} alt={p.provider_name} title={p.provider_name} className="w-7 h-7 rounded-lg shadow-md border border-border/30" />
                )
              ))}
            </div>
          )}
          {movie.runtime > 0 && (
            <div className="absolute top-3 right-3 px-2 py-1 rounded-lg bg-background/70 backdrop-blur-sm border border-border/30">
              <span className="text-[11px] font-medium text-foreground tabular-nums">{movie.runtime >= 60 ? `${Math.floor(movie.runtime / 60)}h ${movie.runtime % 60}m` : `${movie.runtime}m`}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            {mediaType === "tv" && onCurrentlyWatching && (
              <button
                onClick={onCurrentlyWatching}
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] uppercase tracking-wider font-semibold transition-all active:scale-95 mb-1.5 backdrop-blur-md ${
                  isCurrentlyWatching
                    ? "bg-primary/40 text-primary-foreground"
                    : "bg-muted/60 text-muted-foreground hover:bg-primary/30 hover:text-primary-foreground"
                }`}
              >
                📺 {t("discover.currentlyWatchingBtn")}
              </button>
            )}
            <div className="flex items-center gap-2 mb-1">
              {movie.genres.map((g) => (<span key={g} className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/40 text-primary-foreground backdrop-blur-md font-semibold">{g}</span>))}
            </div>
            <h3 className="font-display text-xl font-bold text-foreground leading-tight">{movie.title}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {movie.year} · ⭐ {movie.rating}
              {movie.certification && (<span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-[11px] font-semibold text-muted-foreground border border-border/50 align-middle">{movie.certification}</span>)}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <p className="text-sm text-secondary-foreground line-clamp-2 leading-relaxed flex-1">{movie.synopsis}</p>
              {onHide && (
                <button
                  onClick={onHide}
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                    isHidden
                      ? "bg-destructive/30 text-destructive"
                      : "bg-muted/60 text-muted-foreground hover:bg-destructive/20 hover:text-destructive"
                  }`}
                  title={t("discover.dontRecommend")}
                >
                  <EyeOff className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {onReadMore && (<button onClick={onReadMore} className="text-xs text-primary font-medium mt-1 active:scale-95 transition-transform">{t("discover.readMore")}</button>)}
          </div>
        </div>

        <div className="p-2 flex items-center justify-between shrink-0">
          {!hideSeen ? (
            <div className="flex items-center gap-1">
              <button onClick={() => onSeen("hated")} className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all active:scale-95 ${seenRating === "hated" ? "bg-sentiment-nah/30 ring-2 ring-sentiment-nah" : "bg-sentiment-nah/10 hover:bg-sentiment-nah/20"}`}>
                <span className="text-base">😡</span><span className="text-[10px] font-medium text-sentiment-nah">{t("discover.hated")}</span>
              </button>
              <button onClick={() => onSeen("not_great")} className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all active:scale-95 ${seenRating === "not_great" ? "bg-sentiment-not-great/30 ring-2 ring-sentiment-not-great" : "bg-sentiment-not-great/10 hover:bg-sentiment-not-great/20"}`}>
                <span className="text-base">😕</span><span className="text-[10px] font-medium text-sentiment-not-great">{t("discover.notGreat")}</span>
              </button>
              <button onClick={() => onSeen("ok")} className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all active:scale-95 ${seenRating === "ok" ? "bg-sentiment-ok/30 ring-2 ring-sentiment-ok" : "bg-sentiment-ok/10 hover:bg-sentiment-ok/20"}`}>
                <span className="text-base">😐</span><span className="text-[10px] font-medium text-sentiment-ok">{t("discover.ok")}</span>
              </button>
              <button onClick={() => onSeen("pretty_good")} className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all active:scale-95 ${seenRating === "pretty_good" ? "bg-sentiment-pretty-good/30 ring-2 ring-sentiment-pretty-good" : "bg-sentiment-pretty-good/10 hover:bg-sentiment-pretty-good/20"}`}>
                <span className="text-base">😊</span><span className="text-[10px] font-medium text-sentiment-pretty-good">{t("discover.good")}</span>
              </button>
              <button onClick={() => onSeen("loved")} className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all active:scale-95 ${seenRating === "loved" ? "bg-sentiment-love/30 ring-2 ring-sentiment-love" : "bg-sentiment-love/10 hover:bg-sentiment-love/20"}`}>
                <span className="text-base">😍</span><span className="text-[10px] font-medium text-sentiment-love">{t("discover.loved")}</span>
              </button>
            </div>
          ) : <div />}

          <div className="flex items-center gap-2">
            <button onClick={onStar} className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-all active:scale-95 ${isStarred ? "bg-star/30 ring-2 ring-star" : "bg-star/10 hover:bg-star/20"}`}>
              <Star className={`w-5 h-5 text-star ${isStarred ? "fill-star" : ""}`} /><span className="text-[10px] font-medium text-star">{t("discover.pick")}</span>
            </button>
            {!hideBookmark && (
              <button onClick={onBookmark} className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-all active:scale-95 ${isBookmarked ? "bg-bookmark/30 ring-2 ring-bookmark" : "bg-bookmark/10 hover:bg-bookmark/20"}`}>
                <Bookmark className={`w-5 h-5 text-bookmark ${isBookmarked ? "fill-bookmark" : ""}`} /><span className="text-[10px] font-medium text-bookmark">{t("discover.saveAction")}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SwipeCard;
